import React, { useState, useEffect, useCallback } from 'react';
import { templatesApi, ontologyApi } from '../api';
import StatusBadge from '../shared/StatusBadge';
import ConfirmDialog from '../shared/ConfirmDialog';

const NARRATIVE_TYPES = ['event', 'entity', 'relationship', 'composite'];

const EMPTY = {
  workspace_id: '',
  entity_type: '',
  event_type: '',
  trigger_condition: '',
  template: '',
  priority: 100,
  narrative_type: 'event',
  is_active: true,
};

export default function TemplatesTab({ workspaceId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY, workspace_id: workspaceId });
  const [deleteId, setDeleteId] = useState(null);
  const [clearAllOpen, setClearAllOpen] = useState(false);

  // Auto-generate state
  const [generating, setGenerating] = useState(false);
  const [confirmVersion, setConfirmVersion] = useState(null);
  const [genResult, setGenResult] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const data = await templatesApi.list(workspaceId);
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, workspace_id: workspaceId };
      if (editing) {
        await templatesApi.update(editing, payload);
      } else {
        await templatesApi.create(payload);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ ...EMPTY, workspace_id: workspaceId });
      load();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = (t) => {
    setForm(t);
    setEditing(t.template_id);
    setShowForm(true);
  };

  const handleDelete = async () => {
    try {
      await templatesApi.delete(deleteId);
      setDeleteId(null);
      load();
    } catch (err) { alert('Delete error: ' + (err.response?.data?.detail || err.message)); }
  };

  const handleClearAll = async () => {
    setClearAllOpen(false);
    setLoading(true);
    try {
      const r = await templatesApi.deleteAll(workspaceId);
      setGenResult({ type: 'success', text: `Deleted ${r.deleted} template(s).` });
      load();
    } catch (err) {
      alert('Clear all error: ' + (err.response?.data?.detail || err.message));
      setLoading(false);
    }
  };

  // Auto-generate: find latest ontology version then confirm
  const handleGenerate = async () => {
    setGenResult(null);
    try {
      const versions = await ontologyApi.listVersions(workspaceId);
      if (!versions || !versions.length) {
        alert('No ontology versions found. Synthesize an ontology first (Ontology tab).');
        return;
      }
      const latest = versions[versions.length - 1];
      setConfirmVersion(latest);
    } catch (err) {
      alert('Failed to load ontology versions: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleConfirmGenerate = async () => {
    const v = confirmVersion;
    setConfirmVersion(null);
    setGenerating(true);
    setGenResult(null);
    try {
      const r = await templatesApi.autoGenerate(workspaceId, v.version_id);
      setGenResult({ type: 'success', text: `Generated ${r.generated} template(s) from ontology v${v.version_number}.` });
      load();
    } catch (err) {
      setGenResult({ type: 'error', text: 'Generation failed: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setGenerating(false);
    }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const truncate = (s, n) => s && s.length > n ? s.slice(0, n) + '…' : s;

  return (
    <div>
      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Narrative Templates</h2>
          <div className="ednl-btn-group">
            <button
              className="ednl-btn ednl-btn--primary"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? '⏳ Generating…' : 'Generate from Ontology'}
            </button>
            <button className="ednl-btn ednl-btn--secondary" onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ ...EMPTY, workspace_id: workspaceId }); }}>
              {showForm ? 'Cancel' : '+ New Template'}
            </button>
            {templates.length > 0 && (
              <button className="ednl-btn ednl-btn--danger ednl-btn--sm" onClick={() => setClearAllOpen(true)}>
                Clear All ({templates.length})
              </button>
            )}
          </div>
        </div>

        {genResult && (
          <div className={`ednl-inline-alert ednl-inline-alert--${genResult.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
            {genResult.text}
            <button style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }} onClick={() => setGenResult(null)}>✕</button>
          </div>
        )}

        {generating && (
          <div className="ednl-loading" style={{ marginBottom: 16 }}>
            <span className="ednl-spinner" /> Generating narrative templates via LLM — this may take 10–30 seconds…
          </div>
        )}

        {showForm && (
          <form className="ednl-form" onSubmit={handleSubmit} style={{ marginBottom: 20 }}>
            <div className="ednl-form-row">
              <div className="ednl-form-group">
                <label className="ednl-label">Entity Type</label>
                <input className="ednl-input" value={form.entity_type} onChange={e => set('entity_type', e.target.value)} required placeholder="Transactions, *, etc." />
              </div>
              <div className="ednl-form-group">
                <label className="ednl-label">Event Type</label>
                <input className="ednl-input" value={form.event_type} onChange={e => set('event_type', e.target.value)} required />
              </div>
              <div className="ednl-form-group">
                <label className="ednl-label">Narrative Type</label>
                <select className="ednl-select" value={form.narrative_type} onChange={e => set('narrative_type', e.target.value)}>
                  {NARRATIVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="ednl-form-group" style={{ maxWidth: 100 }}>
                <label className="ednl-label">Priority</label>
                <input className="ednl-input" type="number" value={form.priority} onChange={e => set('priority', parseInt(e.target.value) || 100)} />
              </div>
            </div>
            <div className="ednl-form-group">
              <label className="ednl-label">Trigger Condition (Jinja2 bool expression)</label>
              <input className="ednl-input" value={form.trigger_condition} onChange={e => set('trigger_condition', e.target.value)} placeholder='change.amount.new_value > 10000' />
            </div>
            <div className="ednl-form-group">
              <label className="ednl-label">Jinja2 Template</label>
              <textarea className="ednl-textarea" rows={6} value={form.template} onChange={e => set('template', e.target.value)} required placeholder={'Transaction {{ row.transaction_id }} for ${{ row.amount }}\n{% if change.transaction_type %}\nFlagged as {{ row.transaction_type }}\n{% endif %}'} />
            </div>
            <div>
              <button className="ednl-btn ednl-btn--primary" type="submit">{editing ? 'Update' : 'Create'}</button>
            </div>
          </form>
        )}

        {loading ? <div className="ednl-loading">Loading…</div> : templates.length === 0 ? (
          <div className="ednl-empty"><div className="ednl-empty__icon">📝</div><div className="ednl-empty__text">No templates yet. Click "Generate from Ontology" to auto-create templates.</div></div>
        ) : (
          <div className="ednl-table-wrap">
            <table className="ednl-table">
              <thead><tr><th>Entity</th><th>Event</th><th>Type</th><th style={{ minWidth: 220 }}>Template Preview</th><th>Pri</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t.template_id}>
                    <td>{t.entity_type}</td>
                    <td>{t.event_type}</td>
                    <td><StatusBadge status="info" label={t.narrative_type} /></td>
                    <td><code style={{ fontSize: 'var(--ednl-font-sm, 12px)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{truncate(t.template, 90)}</code></td>
                    <td>{t.priority}</td>
                    <td><StatusBadge status={t.is_active ? 'active' : 'inactive'} label={t.is_active ? 'Active' : 'Off'} /></td>
                    <td>
                      <div className="ednl-btn-group">
                        <button className="ednl-btn ednl-btn--sm" onClick={() => handleEdit(t)}>Edit</button>
                        <button className="ednl-btn ednl-btn--sm ednl-btn--danger" onClick={() => setDeleteId(t.template_id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirm single delete */}
      <ConfirmDialog open={!!deleteId} title="Delete Template" message="Are you sure you want to delete this template?" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />

      {/* Confirm clear all */}
      <ConfirmDialog
        open={clearAllOpen}
        title="Clear All Templates"
        message={`Delete all ${templates.length} template(s) for this workspace? This cannot be undone.`}
        onConfirm={handleClearAll}
        onCancel={() => setClearAllOpen(false)}
      />

      {/* Confirm auto-generate from ontology */}
      <ConfirmDialog
        open={!!confirmVersion}
        title="Generate Narrative Templates"
        message={confirmVersion
          ? `Found ontology v${confirmVersion.version_number} with ${confirmVersion.entity_types?.length ?? '?'} entities and ${confirmVersion.relationships?.length ?? '?'} relationships.\n\nGenerate narrative templates from this ontology?`
          : ''}
        onConfirm={handleConfirmGenerate}
        onCancel={() => setConfirmVersion(null)}
      />
    </div>
  );
}
